import { Group, NumberInput, Stack, Text } from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { FormattedMessage, useIntl } from 'react-intl'
import { LabelWithHint } from '@/components/ui-shared/LabelWithHint/LabelWithHint'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import {
  MAX_CACHE_SECONDS,
  MAX_EVENT_VIDEO_DURATION_TIME,
  MAX_PRE_EVENT_SECONDS,
  MIN_CACHE_SECONDS,
  MIN_EVENT_VIDEO_DURATION_TIME,
  MIN_PRE_EVENT_SECONDS
} from '../../LogicSelection/LogicHandler/constants'
import { FormValues } from '../DeploymentSettings'
import { SettingsCard } from '../SettingsCard'

type EventRecordingSettingsProps = {
  form: UseFormReturnType<FormValues>
}

const toSeconds = (value: number | string) => Number(value) || 0

export const EventRecordingSettings = ({
  form
}: EventRecordingSettingsProps) => {
  const intl = useIntl()
  const { runInGroups, preEventSeconds, eventVideoDurationTime } =
    form.getValues()

  const clipLength =
    toSeconds(preEventSeconds) + toSeconds(eventVideoDurationTime)

  const durationInput = (
    <NumberInput
      {...form.getInputProps('eventVideoDurationTime')}
      label={
        <LabelWithHint
          label={<FormattedMessage id="events.video.duration" />}
          hint={<FormattedMessage id="events.video.duration.tooltip" />}
        />
      }
      description={
        <FormattedMessage
          id="events.video.duration.hint"
          values={{
            min: MIN_EVENT_VIDEO_DURATION_TIME,
            max: MAX_EVENT_VIDEO_DURATION_TIME
          }}
        />
      }
      inputWrapperOrder={['label', 'input', 'description', 'error']}
    />
  )

  return (
    <SettingsCard
      title={<FormattedMessage id="events.recording" />}
      contentMaxWidth={480}
    >
      {runInGroups ? (
        <Stack gap="md">
          <Group align="flex-start" grow>
            <NumberInput
              {...form.getInputProps('preEventSeconds')}
              label={
                <LabelWithHint
                  label={<FormattedMessage id="events.video.preEvent.label" />}
                  hint={<FormattedMessage id="events.video.preEvent.tooltip" />}
                />
              }
              description={
                <FormattedMessage
                  id="events.video.duration.hint"
                  values={{
                    min: MIN_PRE_EVENT_SECONDS,
                    max: MAX_PRE_EVENT_SECONDS
                  }}
                />
              }
              inputWrapperOrder={['label', 'input', 'description', 'error']}
            />

            {durationInput}
          </Group>

          <Text size="sm">
            <FormattedMessage
              id="events.recording.clipLength"
              values={{ total: clipLength }}
            />
          </Text>

          <NumberInput
            {...form.getInputProps('cacheSeconds')}
            placeholder={intl.formatMessage({
              id: 'events.video.cache.placeholder'
            })}
            label={
              <LabelWithHint
                label={<FormattedMessage id="events.video.cache.label" />}
                hint={<FormattedMessage id="events.video.cache.tooltip" />}
              />
            }
            description={
              <FormattedMessage
                id="events.video.cache.hint"
                values={{ min: MIN_CACHE_SECONDS, max: MAX_CACHE_SECONDS }}
              />
            }
            inputWrapperOrder={['label', 'input', 'description', 'error']}
          />

          <PlatformAlert variant="info" compact>
            <FormattedMessage id="events.recording.groupsInfo" />
          </PlatformAlert>
        </Stack>
      ) : (
        durationInput
      )}
    </SettingsCard>
  )
}
